import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Inbox, RefreshCw, PhoneCall, CheckCircle2, Archive, MapPin } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  new: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  contacted: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  converted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  archived: 'bg-slate-100 text-slate-500 dark:bg-cn-ink-700 dark:text-slate-400',
};
const FILTERS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

const Tile = ({ testid, label, value, tone }) => (
  <div data-testid={testid} className="p-4 rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800">
    <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
    <p className={`text-2xl font-bold font-display mt-1 ${tone}`}>{value}</p>
  </div>
);

export const LeadsPanel = () => {
  const { token } = useAuth();
  const [data, setData] = useState({ leads: [], summary: {} });
  const [filter, setFilter] = useState('new');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === 'all' ? '' : `?status=${filter}`;
      const { data } = await axios.get(`${API}/admin/leads${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(data);
    } catch (e) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const setStatus = async (lead, status) => {
    setBusyId(lead.id);
    try {
      await axios.patch(`${API}/admin/leads/${lead.id}`, { status }, authHeader);
      toast.success(`${lead.name} marked ${status}`);
      await load();
    } catch (e) {
      toast.error('Could not update lead');
    } finally {
      setBusyId(null);
    }
  };

  const s = data.summary || {};

  return (
    <div data-testid="leads-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="leads-summary">
        <Tile testid="leads-stat-new" label="New leads" value={s.new ?? 0} tone="text-blue-600" />
        <Tile testid="leads-stat-contacted" label="Contacted" value={s.contacted ?? 0} tone="text-amber-600" />
        <Tile testid="leads-stat-converted" label="Converted" value={s.converted ?? 0} tone="text-emerald-600" />
        <Tile testid="leads-stat-archived" label="Archived" value={s.archived ?? 0} tone="text-slate-500" />
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.value} onClick={() => setFilter(f.value)} data-testid={`leads-filter-${f.value}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f.value ? 'bg-cn-primary text-white' : 'bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={load} data-testid="leads-refresh-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300 hover:text-cn-primary">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary" /></div>
      ) : data.leads.length === 0 ? (
        <div className="py-12 text-center" data-testid="leads-empty">
          <Inbox className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">No leads in this view</h3>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Leads captured from the local SEO city pages will appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-cn-ink-700">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-cn-ink-700">
              <th className="px-4 py-3 font-bold">Name</th><th className="px-4 py-3 font-bold">Care need</th>
              <th className="px-4 py-3 font-bold">Location</th><th className="px-4 py-3 font-bold">Source</th>
              <th className="px-4 py-3 font-bold">Received</th><th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3 font-bold text-right">Actions</th>
            </tr></thead>
            <tbody>
              {data.leads.map((l) => {
                const busy = busyId === l.id;
                return (
                  <tr key={l.id} className="border-b border-slate-100 dark:border-cn-ink-700/60 last:border-0" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                      {l.name}
                      {l.email && <div className="text-xs text-slate-400 font-normal">{l.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{l.care_need}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {l.city ? `${l.city}, ` : ''}{l.state} {l.zip_code}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-400 capitalize">{(l.source || '').replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{(l.created_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${STATUS_STYLE[l.status] || ''}`}>{l.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {l.status !== 'contacted' && l.status !== 'converted' && (
                          <button onClick={() => setStatus(l, 'contacted')} disabled={busy} title="Mark contacted" data-testid={`lead-contacted-${l.id}`}
                            className="p-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 disabled:opacity-50">
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PhoneCall className="w-3.5 h-3.5" />}
                          </button>
                        )}
                        {l.status !== 'converted' && (
                          <button onClick={() => setStatus(l, 'converted')} disabled={busy} title="Mark converted" data-testid={`lead-converted-${l.id}`}
                            className="p-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 disabled:opacity-50">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {l.status !== 'archived' && (
                          <button onClick={() => setStatus(l, 'archived')} disabled={busy} title="Archive" data-testid={`lead-archived-${l.id}`}
                            className="p-1.5 rounded-lg bg-slate-100 text-slate-500 hover:text-red-600 dark:bg-cn-ink-700 disabled:opacity-50">
                            <Archive className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LeadsPanel;
